import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Modal, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { connect, useDispatch } from "react-redux";
import api, {
  firstLoginPromoFallback,
  standardSixMonthCheckoutProgramId,
} from "../constants/config";
import SnackBar from "../atoms/snackBar";
import { setUserData } from "../redux/action/user";
import { isSubscriptionActive } from "../common";

const DEFAULT_ENDPOINTS = {
  getOffer: "me/first-login-offer",
  dismissOffer: "me/first-login-offer/dismiss",
  createPayCheckout: "create-pay-checkout",
};

/** Backend may send shouldShow as boolean, 1/0, or string. */
function isAffirmative(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function pickPromoFromPayload(data) {
  if (!data || typeof data !== "object") return { code: "", label: "" };
  const code = (
    data.promoCode ??
    data.code ??
    data.couponCode ??
    data.promotionCode ??
    data.stripePromotionCode ??
    ""
  )
    .toString()
    .trim();
  const label = (data.discountLabel ?? data.label ?? "").toString().trim();
  return { code, label };
}

function pickShouldShowField(data) {
  if (!data || typeof data !== "object") return undefined;
  return (
    data.shouldShow ??
    data.showOffer ??
    data.show ??
    data.should_show ??
    data.should_display ??
    data.display ??
    undefined
  );
}

function messageFromApiError(err) {
  if (err == null) return null;
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    if (typeof err.message === "string" && err.message) return err.message;
    if (typeof err.error === "string" && err.error) return err.error;
    if (err.error && typeof err.error.message === "string") return err.error.message;
    if (typeof err.msg === "string" && err.msg) return err.msg;
  }
  return null;
}

const FirstLoginUpgradeModal = (props) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [discountLabel, setDiscountLabel] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const token = localStorage.getItem("userKey");
  const isSubscribed = isSubscriptionActive(props?.user);

  const endpoints = useMemo(() => DEFAULT_ENDPOINTS, []);

  // Once opened, keep open until user action (dismiss/upgrade).
  // This avoids dev StrictMode double-effect or any re-fetch from auto-closing.
  const holdOpenUntilUserActionRef = useRef(false);

  const safeClose = () => {
    holdOpenUntilUserActionRef.current = false;
    setOpen(false);
    dispatch(
      setUserData({
        isLoggedinModal: false,
      })
    );
  };

  const fetchOffer = async () => {
    if (!token || isSubscribed) return;

    setIsLoading(true);
    if (!holdOpenUntilUserActionRef.current) {
      setPromoCode("");
      setDiscountLabel("");
    }
    try {
      const res = await api.get(endpoints.getOffer);
      const data = res?.data?.data ?? res?.data ?? {};

      let { code, label } = pickPromoFromPayload(data);
      if (!code && firstLoginPromoFallback) code = firstLoginPromoFallback;
      const showField = pickShouldShowField(data);
      const explicitlyHidden =
        showField === false ||
        showField === 0 ||
        showField === "0" ||
        showField === "false";
      const shouldShow = !explicitlyHidden && isAffirmative(showField);

      // If we've already opened, keep it open until user action.
      if (holdOpenUntilUserActionRef.current) {
        setOpen(true);
        if (code) setPromoCode(code);
        if (label) setDiscountLabel(label);
        return;
      }

      if (shouldShow) {
        holdOpenUntilUserActionRef.current = true;
        setOpen(true);
        if (code) setPromoCode(code);
        if (label) setDiscountLabel(label);
      } else if (!holdOpenUntilUserActionRef.current) {
        setOpen(false);
        dispatch(setUserData({ isLoggedinModal: false }));
      }
    } catch (e) {
      if (!holdOpenUntilUserActionRef.current) {
        setOpen(false);
        dispatch(setUserData({ isLoggedinModal: false }));
      }
      const msg = messageFromApiError(e) || "Could not load first-login offer.";
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isSubscribed) {
      setOpen(false);
      dispatch(setUserData({ isLoggedinModal: false }));
    }
  }, [isSubscribed, dispatch]);

  useEffect(() => {
    if (!token || isSubscribed) return;
    fetchOffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isSubscribed]);

  const dismissOffer = async () => {
    try {
      await api.post(endpoints.dismissOffer);
    } catch {
      // Ignore; FE will still close.
    } finally {
      safeClose();
    }
  };

  const upgradeToStandard6mo = async () => {
    setCheckoutLoading(true);
    try {
      const couponFromModal = promoCode && String(promoCode).trim();
      const couponFromStorage = localStorage.getItem("couponCode");
      const coponCode =
        couponFromModal || (couponFromStorage && couponFromStorage.trim()) || null;

      const payload = {
        ID: standardSixMonthCheckoutProgramId,
        coponCode,
      };

      const res = await api.post(endpoints.createPayCheckout, payload);
      const url =
        res?.data?.data?.url ??
        res?.data?.url ??
        res?.data?.data?.sessionUrl ??
        res?.data?.sessionUrl;
      if (url) {
        safeClose();
        window.location.href = url;
      } else {
        setSnack({
          open: true,
          message: "Checkout URL missing from server response.",
          severity: "error",
        });
      }
    } catch (err) {
      const msg =
        messageFromApiError(err) ||
        "Could not start checkout. Check program ID and create-pay-checkout on the server.";
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <Fragment>
      <Modal
        open={open}
        onClose={() => {}}
        disableEscapeKeyDown
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <Box sx={offerModalStyle}>
          {/* Top bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.25,
              background: "#b99a57",
              color: "#0b241e",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              fontWeight: 700,
            }}
          >
            <Box>One-time offer · disappears when you leave this screen</Box>
            <CloseIcon
              onClick={dismissOffer}
              sx={{ cursor: "pointer", fontSize: "18px" }}
            />
          </Box>

          {/* Body */}
          <Box sx={{ px: 3, pt: 3, pb: 2.5, background: "#0f2a23", color: "#f3efe2" }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                px: 1.5,
                py: 0.5,
                borderRadius: "999px",
                border: "1px solid rgba(201, 173, 106, 0.6)",
                color: "#c9ad6a",
                fontSize: "0.75rem",
                fontWeight: 700,
                mb: 2,
              }}
            >
              50% OFF THE TRANSFORMATION PLAN
            </Box>

            <Typography
              sx={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontWeight: 500,
                fontSize: { xs: "1.8rem", sm: "2.2rem" },
                lineHeight: 1.15,
                mb: 1.5,
              }}
            >
              You've seen your scores.
              <br />
              Now let's elevate them.
            </Typography>

            <Typography sx={{ color: "rgba(243, 239, 226, 0.85)", mb: 2.5 }}>
              Six months from now, everything can be different. This is your only chance to
              lock in half price — for life.
            </Typography>

            {/* Price row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                gap: 1.5,
                flexWrap: "wrap",
                mb: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: { xs: "2.3rem", sm: "2.8rem" },
                  lineHeight: 1,
                  color: "#f3efe2",
                }}
              >
                $119.49
              </Typography>
              <Typography sx={{ mb: 0.45, color: "rgba(243, 239, 226, 0.85)" }}>
                every 6 months
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.35 }}>
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.35,
                    borderRadius: "999px",
                    background: "#b99a57",
                    color: "#0b241e",
                    fontWeight: 800,
                    fontSize: "0.75rem",
                  }}
                >
                  50% off
                </Box>
                <Typography
                  sx={{
                    textDecoration: "line-through",
                    color: "rgba(243, 239, 226, 0.6)",
                    mb: 0.1,
                  }}
                >
                  $234
                </Typography>
              </Box>
            </Box>

            <Typography sx={{ color: "rgba(243, 239, 226, 0.7)", mb: 2.5 }}>
              ${/* price/terms are marketing copy; keep static */}19.92/month · renews automatically every 6 months — cancel anytime before your next renewal date
            </Typography>

            {/* Benefits */}
            <Box sx={{ display: "grid", gap: 1.5, mb: 3 }}>
              {[
                "Full course library — Vibrant Life Method, Vibrant Plate, Foundations of Health, and more",
                "Weekly live office hours with our MDs and PhD — bring your exact questions",
                "43% off 94-biomarker lab panel, ebook library, yoga, recipes, and private community",
              ].map((text) => (
                <Box key={text} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      mt: "2px",
                      width: 22,
                      height: 22,
                      borderRadius: "999px",
                      background: "rgba(201, 173, 106, 0.2)",
                      border: "1px solid rgba(201, 173, 106, 0.55)",
                      display: "grid",
                      placeItems: "center",
                      color: "#c9ad6a",
                      fontWeight: 900,
                      flex: "0 0 auto",
                    }}
                  >
                    ✓
                  </Box>
                  <Typography sx={{ color: "rgba(243, 239, 226, 0.9)", lineHeight: 1.35 }}>
                    {text}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* CTA */}
            <Button
              fullWidth
              variant="contained"
              onClick={upgradeToStandard6mo}
              disabled={isLoading || checkoutLoading}
              sx={{
                background: "#b99a57",
                color: "#0b241e",
                fontWeight: 800,
                textTransform: "none",
                py: 1.4,
                borderRadius: "6px",
                "&:hover": { background: "#a98a4a" },
              }}
            >
              {checkoutLoading ? "Opening checkout…" : "Lock in 50% off — $119.49 for 6 months"}
            </Button>

            <Typography
              sx={{
                mt: 1.25,
                fontSize: "0.85rem",
                textAlign: "center",
                color: "rgba(243, 239, 226, 0.7)",
              }}
            >
              This offer disappears the moment you leave this screen.
            </Typography>

            <Button
              variant="text"
              onClick={dismissOffer}
              sx={{
                mt: 0.75,
                width: "100%",
                textTransform: "none",
                color: "rgba(201, 173, 106, 0.95)",
                textDecoration: "underline",
                "&:hover": { textDecoration: "underline", background: "transparent" },
              }}
            >
              No thanks
            </Button>
          </Box>
        </Box>
      </Modal>

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        handleCloseSnackBar={() => setSnack({ open: false, message: "", severity: "success" })}
      />
    </Fragment>
  );
};

export default connect(({ user }) => ({ user }))(FirstLoginUpgradeModal);

const offerModalStyle = {
  width: "min(680px, 94vw)",
  maxHeight: "92vh",
  overflow: "auto",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  boxShadow: 24,
  borderRadius: "10px",
  border: "1px solid rgba(201, 173, 106, 0.35)",
};

